/* eslint-disable @typescript-eslint/no-explicit-any */
import apiRoutes from "@/src/apiRoutes";
import { Button } from "@/src/components/Button/Button";
import InputComponent from "@/src/components/Input/Input";
import { Modal } from "@/src/components/Modal/Modal";
import { Label, textVariants } from "@/src/components/Text/Text";
import useUrlState from "@/src/hooks/use-url-state";
import { useWalletData } from "@/src/hooks/useCoinData";
import api, { formatNumber, getUniqueArrayByKey } from "@/src/utils";
import { useMutation } from "@tanstack/react-query";
import _, { isEmpty } from "lodash";
import { SubmitHandler, useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import TradeInput from "@/src/components/TradeInput";
import TransferVertical from "@/src/svg/transferVertical";
import { useTheme } from "@/src/context/themeContext";
import { useTranslation } from "react-i18next";
import { TOptions } from "i18next";
import { Combobox } from "@/src/components/combobox";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCoinBalance } from "@/src/hooks/useCoinBalance";
import { cn } from "@/lib/utils";
import { MiniLoader } from "@/src/components/Loader";

const formState = z.object({
  coin: z.string(),
  address: z.string().min(1),
  duration: z.string(),
  amount_type: z.string(),
  amount: z.string(),
  max: z.boolean().optional(),
  code: z.string().optional(),
  network: z.string().optional(),
});

const Withdraw = () => {
  type withdrawType = z.infer<typeof formState>;
  const { t } = useTranslation();
  const trans = (key: string, option?: TOptions) =>
    t(`app.wallet.withdraw.${key}`, option);
  const { theme } = useTheme();
  const [modal, setModal] = useUrlState("modal");
  const { walletData, isLoading } = useWalletData("withdrawal");
  const { register, setValue, watch, getValues, handleSubmit } =
    useForm<withdrawType>({
      defaultValues: {
        amount_type: "fiat",
        duration: "NORMAL",
      },
      resolver: zodResolver(formState),
    });
  const selectedCoin = walletData?.wallets?.find(
    (item) => item?.coin__id === Number(watch("coin"))
  );

  const tickerInstance = walletData?.wallets?.filter(
    (item) => item?.ticker === selectedCoin?.ticker
  );

  const uniqueCoins =
    walletData &&
    getUniqueArrayByKey<WalletDataInfo>(walletData?.wallets as [], "ticker");

  const checkRate = async () => {
    const values = _.omit(getValues(), ["coin", "amount_type", "network"]);
    const res = await api.post(apiRoutes.checkEstimate, {
      ...values,
      coin:
        tickerInstance && tickerInstance?.length > 1
          ? watch("network")
          : selectedCoin?.coin__id,
      amount_type: watch("amount_type"),
      network: walletData?.wallets?.find(
        (item) => item?.coin__id === Number(watch("coin"))
      )?.network,
    });
    return res.data;
  };

  const {
    mutate,
    isLoading: checkLoad,
    data,
    // reset,
  } = useMutation<CheckEstimate>({
    mutationKey: ["check_estimate"],
    mutationFn: checkRate,
    onSuccess: (data) => {
      if (data?.success) {
        // toast.success(data?.info)
        setModal("preview");
      } else {
        toast.warning(data?.info as any);
      }
    },
  });

  const withdrawFn = async () => {
    const res = await api.post(
      apiRoutes.validateEstimate,
      watch("code")
        ? {
            type: "validate",
            code: getValues().code,
          }
        : {
            type: "validate",
          }
    );
    return res.data;
  };

  const { mutate: mutateWithdraw, isLoading: withdrawLoad } = useMutation({
    mutationFn: withdrawFn,
    onSuccess: (data) => {
      if (data?.success) {
        toast.success(data?.info);
        setModal(undefined);
      } else {
        toast.warning(data?.info);
      }
    },
  });

  const requestCode = async () => {
    const res = await api.post(apiRoutes.validateEstimate, {
      type: "request",
    });
    return res.data;
  };

  const { mutate: requestCodeMutate, isLoading: requestCodeLoad } = useMutation(
    {
      mutationKey: ["request_code"],
      mutationFn: requestCode,
      onSuccess: (data) => {
        if (data?.success) {
          toast.success(data?.info);
          setModal("open");
        } else {
          toast.warning(data?.info);
        }
      },
    }
  );

  const triggerPreview: SubmitHandler<withdrawType> = () => {
    mutate();
  };

  const { balance } = useCoinBalance(
    Number(watch("network")) && tickerInstance && tickerInstance.length > 1
      ? Number(watch("network"))
      : Number(watch("coin"))
  );

  if (isLoading) return <MiniLoader />;
  return (
    <div>
      <div className="grid gap-5">
        <h2
          className={textVariants({
            variant: "header",
            font: "semibold",
            size: "xl",
          })}
        >
          {trans("header")}
        </h2>
        <form className="grid gap-5" onSubmit={handleSubmit(triggerPreview)}>
          {uniqueCoins && (
            <div>
              <Label>{trans("form.coin.label")}</Label>
              <Combobox
                triggerClassName="h-16 rounded-xl"
                items={uniqueCoins?.map((coin) => {
                  return {
                    searchText: coin?.label,
                    render: (
                      <div className="flex items-center gap-2 w-full ">
                        <img
                          className="h-6 w-6 "
                          src={coin?.image}
                          alt="crypto"
                        />
                        <div className="w-full flex items-center">
                          <div className="grid">
                            <span className="font-bold text-base text-left">
                              {coin?.label}
                            </span>
                            {/* {balance?.info.fiat &&
                              coin?.coin__id === Number(watch("coin")) && (
                                <span className="font-normal text-sm">
                                  {balance?.info.crypto}{" "}
                                  {coin?.ticker.toUpperCase()} ={" "}
                                  {Number(balance?.info.fiat).toFixed(2)} USD{" "}
                                </span>
                              )} */}
                          </div>
                        </div>
                      </div>
                    ),
                    value: coin?.coin__id.toString(),
                  };
                })}
                emptyMessage="No items found."
                onChange={(val) => setValue("coin", val)}
                placeholder={trans("form.coin.placeholder")}
              />
              {tickerInstance && tickerInstance?.length === 1 && (
                <span
                  className={cn(
                    textVariants({ variant: "link" }),
                    "font-normal text-sm uppercase"
                  )}
                >
                  {balance?.info.crypto} {selectedCoin?.ticker} ={" "}
                  {Number(balance?.info.fiat).toFixed(2)} USD{" "}
                </span>
              )}
            </div>
          )}
          {tickerInstance && tickerInstance?.length > 1 && (
            <div>
              <Label>{trans("form.network.label")}</Label>
              <Combobox
                triggerClassName="h-16 rounded-xl"
                items={tickerInstance?.map((item) => ({
                  value: item?.coin__id.toString(),
                  render: (
                    <div
                      key={item?.coin__id}
                      className="flex items-center gap-2"
                    >
                      <img
                        src={item?.network_image}
                        alt={item?.ticker}
                        className="h-6 w-6"
                      />
                      <span>{`${item?.label} ${item?.network_label}`}</span>
                    </div>
                  ),
                  searchText: item?.ticker + item?.network_label,
                }))}
                emptyMessage="No items found."
                placeholder={trans("form.network.placeholder", {
                  coin: selectedCoin?.label,
                })}
                onChange={(val) => setValue("network", val)}
              />
              {tickerInstance && tickerInstance?.length > 1 && (
                <span
                  className={cn(
                    textVariants({ variant: "link" }),
                    "font-normal text-sm uppercase"
                  )}
                >
                  {balance?.info.crypto} {selectedCoin?.ticker} ={" "}
                  {formatNumber(balance?.info.fiat ?? 0)} USD{" "}
                </span>
              )}
            </div>
          )}
          <div>
            <TradeInput
              {...register("address", { required: true })}
              className="p-5"
              label={trans("form.address.label")}
              placeholder={
                selectedCoin &&
                trans("form.address.placeholder", {
                  coin: selectedCoin?.label,
                })
              }
              type="text"
            />
          </div>
          <div className="flex gap-2 w-full  ">
            <TradeInput
              disabled={isEmpty(watch("address"))}
              placeholder={trans("form.amount.placeholder")}
              label={trans("form.amount.label")}
              className="p-5"
              onChange={(e) => setValue("amount", e.target.value)}
            />
            <div className="flex justify-end flex-col">
              <button
                disabled={isEmpty(watch("coin"))}
                onClick={() =>
                  setValue(
                    "amount_type",
                    watch("amount_type") === "fiat" ? "crypto" : "fiat"
                  )
                }
                className=" flex cursor-pointer items-center bg-white dark:bg-grey-600 py-4  rounded-xl border border-input disabled:cursor-not-allowed mb-1 px-3"
              >
                <TransferVertical
                  color={theme === "dark" ? "white" : undefined}
                  height={24}
                  width={24}
                />
                {/* <div
                  // onClick={() => {
                  //   setValue(
                  //     "amount_type",
                  //     watch("amount_type") === "fiat" ? "crypto" : "fiat"
                  //   );
                  // }}
                  className="uppercase font-bold text-gray-600 dark:text-white mr-2"
                >
                  {watch("amount_type") === "fiat"
                    ? "USD"
                    : selectedCoin?.ticker}
                </div> */}
                <div className="grid">
                  <span className="uppercase font-bold text-gray-600 dark:text-white  text-sm">
                    {watch("amount_type") === "fiat"
                      ? "USD"
                      : selectedCoin?.ticker}
                  </span>
                  <span className="uppercase  text-gray-600 dark:text-white text-end  text-[11px]">
                    {watch("amount_type") === "fiat"
                      ? selectedCoin?.ticker
                      : "USD"}
                  </span>
                </div>
              </button>
            </div>
          </div>
          {/* <VerticalStepper steps={steps as any} /> */}
          <Button
            loading={checkLoad}
            // onClick={() => mutate()}
            className="w-full mt-5 md:py-4"
          >
            {trans("form.previewButton")}
          </Button>
        </form>
        <Modal
          open={modal === "preview"}
          setOpen={(val) => setModal(val ? undefined : "preview")}
        >
          <div>
            <div className="grid gap-1 mb-4">
              <span className={textVariants({ variant: "desc" })}>
                {trans("preview.body.amount")}
              </span>
              <span className={textVariants({ size: "xxl", font: "semibold" })}>
                {data?.info.amount_to_withdraw_crypto} {data?.info.ticker}
              </span>
            </div>

            <div className="grid gap-2 bg-[#EDF0FA] dark:bg-grey-500 p-4 rounded-md">
              <div className="flex md:flex-row flex-col justify-between white  ">
                <span className={textVariants({ variant: "desc", size: "sm" })}>
                  {trans("preview.body.amount")}
                </span>
                <span className={textVariants({ size: "sm", font: "bold" })}>
                  {data?.info.amount_to_withdraw}
                </span>
              </div>
              <div className="flex md:flex-row flex-col justify-between">
                <span className={textVariants({ variant: "desc", size: "sm" })}>
                  {trans("preview.body.crypto")}
                </span>
                <span className={textVariants({ size: "sm", font: "bold" })}>
                  {data?.info.amount_to_withdraw_crypto} {data?.info.ticker}
                </span>
              </div>
              <div className="flex md:flex-row flex-col justify-between">
                <span className={textVariants({ variant: "desc", size: "sm" })}>
                  {trans("preview.body.fee")}
                </span>
                <span className={textVariants({ size: "sm", font: "bold" })}>
                  {Number(data?.info.total_fees).toFixed(2)}
                </span>
              </div>
              <div className="flex md:flex-row flex-col justify-between">
                <span className={textVariants({ variant: "desc", size: "sm" })}>
                  {trans("preview.body.feeCrypto")}
                </span>
                <span className={textVariants({ size: "sm", font: "bold" })}>
                  {Number(data?.info.total_fees_crypto)} {data?.info.ticker}
                </span>
              </div>
              <div className="flex md:flex-row flex-col justify-between">
                <span className={textVariants({ variant: "desc", size: "sm" })}>
                  {trans("preview.body.total")}
                </span>
                <span className={textVariants({ size: "sm", font: "bold" })}>
                  {data?.info.total_deduction}
                </span>
              </div>
              <div className="flex md:flex-row flex-col justify-between">
                <span className={textVariants({ variant: "desc", size: "sm" })}>
                  {trans("preview.body.address")}
                </span>
                <span className={textVariants({ size: "sm", font: "bold" })}>
                  {_.truncate(data?.info.address, {
                    length: 30,
                  })}
                </span>
              </div>
            </div>
            <Button
              disabled={!data?.success}
              loading={withdrawLoad || requestCodeLoad}
              onClick={() =>
                isEmpty(data?.info?.extra)
                  ? mutateWithdraw()
                  : data?.info?.extra && data?.info?.extra === "email"
                  ? requestCodeMutate()
                  : setModal("open")
              }
              className="py-3 text-base mt-5 w-full md:w-2/3"
            >
              {trans("preview.body.withdrawButton")} {data?.info.ticker}
            </Button>
          </div>
        </Modal>
        <Modal
          open={modal === "open"}
          setOpen={(val) => setModal(val ? undefined : "open")}
          title={trans("preview.otp.header", { medium: data?.info.extra })}
          description={trans("preview.otp.body", { medium: data?.info.extra })}
          footer={
            <div>
              <Button
                loading={withdrawLoad}
                onClick={() => mutateWithdraw()}
                variant={"outline"}
              >
                {trans("preview.otp.submitButton")}
              </Button>
            </div>
          }
          size={"sm"}
        >
          <div>
            <InputComponent
              onChange={(e) => setValue("code", e.target.value)}
              label={trans("preview.otp.input")}
              type="number"
            />
          </div>
        </Modal>
      </div>
    </div>
  );
};

export default Withdraw;
